import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GetProp, Collapse } from 'antd';
import type { TableProps, CollapseProps } from 'antd';
import { App, Button, Card, Spin, Table, Timeline } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { SorterResult } from 'antd/es/table/interface';

import DiffPresenterComponent from './diff-presenter';

import { AppDispatch, RootState } from '../../store';

import { getHumanReadableEventType, getUserActivityObjectName } from '../../shared/utils';

import { UserActivityEvent } from './models';

import withErrorHandler from '../../hoc/with-error-handler';
import axios from '../../shared/axios';

import * as actions from './store/actions';

const { Column } = Table;

export type UserActivityEventsComponentProps = {};

type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: SorterResult<any>['field'];
  sortOrder?: SorterResult<any>['order'];
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

const UserActivityEventsComponent = (props: UserActivityEventsComponentProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { modal } = App.useApp();

  const { userActivityEvents, userActivityEventsLoading, userActivityObjectTimelineLoading, humanReadableEventTypes } = useSelector(
    (state: RootState) => state.userActivityEvents,
  );

  const renderTotal = (total: number, range: [number, number]) => {
    return `Showing ${range[0]}-${range[1]} of ${total} events`;
  };

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: renderTotal,
      current: 1,
      pageSize: 10,
    },
  });

  const [clickedEventId, setClickedEventId] = useState<string | undefined>(undefined);
  const [clickedObjectId, setClickedObjectId] = useState<string | undefined>(undefined);
  const [selectedObjectTimeline, setSelectedObjectTimeline] = useState<
    {
      label: string;
      children: JSX.Element;
      color: string;
    }[]
  >([]);

  const loadData = () => {
    const currentPageIndex = tableParams.pagination?.current ? tableParams.pagination.current - 1 : 1;
    const pageSize = tableParams.pagination?.pageSize ? tableParams.pagination.pageSize : 10;

    dispatch(actions.getUserActivityEvents(currentPageIndex, pageSize)).then((res: any) => {
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: res.totalCount,
        },
      });
    });
  };

  const refresh = () => {
    loadData();
  };

  useEffect(() => {
    dispatch(actions.getHumanReadableEventTypes());
  }, []);

  useEffect(() => {
    if (humanReadableEventTypes && humanReadableEventTypes.length) {
      loadData();
    }
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams?.sortOrder,
    tableParams?.sortField,
    JSON.stringify(tableParams.filters),
    humanReadableEventTypes
  ]);

  const handleTimelineActionClick = (eventId: string, objectId: string) => {
    setSelectedObjectTimeline([]);

    setClickedEventId(eventId);

    setClickedObjectId(objectId);

    dispatch(actions.getUserActivityObjectTimeline(objectId)).then((res: any) => {
      const timelineItems = res.data as Array<UserActivityEvent>;

      const mappedObjectTimeline = timelineItems.map((item, index) => {
        let itemColor = 'blue';

        if (item.eventType.toLowerCase().includes('insert')) {
          itemColor = 'green';
        } else if (item.eventType.toLowerCase().includes('delete')) {
          itemColor = 'red';
        }

        return {
          label: moment(item.eventDateUtc).utc(false).format('MM/DD/YYYY h:mm:ss A'),
          children: (
            <>
              <span style={{ marginRight: '5px' }}>{item.eventType}</span>
              {index + 1 < timelineItems.length && !item.eventType.toLowerCase().includes('delete') ? (
                <Button
                  type="link"
                  style={{ padding: '0', border: 'none', height: '22px' }}
                  onClick={() => handleWhatsChangedButtonClick(index, item, timelineItems)}
                >
                  What's changed?
                </Button>
              ) : null}
            </>
          ),
          color: itemColor,
        };
      });

      setClickedEventId(undefined);

      setSelectedObjectTimeline(mappedObjectTimeline);
    });
  };

  const handleWhatsChangedButtonClick = (currentEventIndex: number, currentEventItem: UserActivityEvent, timelineItems: UserActivityEvent[]) => {
    const nextIndex = currentEventIndex + 1;

    const previousPayloadJson = timelineItems[nextIndex].payloadAfter;
    const currentPayloadJson = currentEventItem.payloadAfter;

    const previousPayload = previousPayloadJson;
    const currentPayload = currentPayloadJson;

    const modalRef = modal.confirm({
      title: 'Object state (previous/current)',
      icon: null,
      width: 1200,
      height: 800,
      closable: true,
      content: (
        <>
          <DiffPresenterComponent {...{ previousPayload, currentPayload }} />
        </>
      ),
      okCancel: false,
      okText: 'Close',
      onOk: () => {
        modalRef.destroy();
      },
    });
  };

  const handleTableChange: TableProps<UserActivityEvent>['onChange'] = (pagination, filters, sorter) => {
    setTableParams({
      pagination: {
        ...tableParams.pagination,
        ...pagination,
      },
      filters,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
  };

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'History Overview',
      collapsible: 'icon',
      extra: <>
        <Button
          type="primary"
          ghost
          icon={<ReloadOutlined />}
          onClick={() => refresh()}
        />
      </>,
      children: <>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: '2 1 0' }}>
            <Table<UserActivityEvent>
              dataSource={userActivityEvents?.data}
              loading={userActivityEventsLoading}
              rowKey="id"
              size="small"
              pagination={tableParams.pagination}
              onChange={handleTableChange}
            >
              <Column
                title="Action"
                key="action"
                width={100}
                render={(_: any, record: UserActivityEvent) => (
                  <Button
                    size="small"
                    type="primary"
                    loading={userActivityObjectTimelineLoading && record.id === clickedEventId}
                    onClick={() => handleTimelineActionClick(record.id, record.objectId)}
                  >
                    Timeline
                  </Button>
                )}
              />
              <Column
                title="Date (UTC)"
                dataIndex="eventDateUtc"
                key="eventDateUtc"
                width={230}
                render={(date) => {
                  return <>{moment(date).utc(false).format('MM/DD/YYYY h:mm:ss A')}</>;
                }}
              />
              <Column title="Type" dataIndex="eventType" key="eventType"
                render={(eventType) => {
                  return <>{getHumanReadableEventType(humanReadableEventTypes, eventType)}</>;
                }} />
              <Column title="Object Name (After Change)" dataIndex="objectId" key="objectId"
                render={(eventType, record) => {
                  return <>{getUserActivityObjectName(record as UserActivityEvent)}</>;
                }} />
            </Table>
          </div>

          <div style={{ flex: '1 1 0', width: '400px', marginLeft: '16px' }}>
            {userActivityObjectTimelineLoading ? <Spin style={{ display: 'flex', justifyContent: 'center' }} /> : null}
            {!userActivityObjectTimelineLoading && selectedObjectTimeline?.length ? (
              <Card size="small" title={`Object Id: ${clickedObjectId}`}>
                <div style={{ padding: '16px', maxHeight: '550px', overflowX: 'hidden', overflowY: 'auto' }}>
                  <Timeline mode="left" items={selectedObjectTimeline} />
                </div>
              </Card>
            ) : null}
          </div>
        </div>
      </>,
    }
  ];

  return (
    <>
      <Collapse items={items} bordered={false} defaultActiveKey={['1']} />
    </>
  );
};

export default withErrorHandler(UserActivityEventsComponent, axios);
